import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';

import { BidAskMidPicker } from '@components/BidAskMidPicker';
import { SlideToConfirmButton } from '@components/SlideToConfirmButton';
import { useAccount } from '@hooks/useAccount';
import { useOptionsPricing } from '@hooks/useOptionsPricing';
import { orderService } from '@services/orderService';
import { RoundService } from '@services/roundService';
import type { Asset } from '@shared/types';
import { formatOptionPrice } from '@utils/price-formatting';

interface TradePanelProps {
  isVisible: boolean;
  tradeType: 'CALL' | 'PUT' | null;
  strikePrice: number;
  spotPrice: number;
  asset: Asset;
  volatility?: number;
  expiryTimestamp?: number;
  side?: 'BUY' | 'SELL';
  maxQuantity?: number;
  onClose: () => void;
  onTrade: (
    amount: number,
    orderType: 'MARKET' | 'LIMIT',
    limitPrice?: number,
    premium?: number,
  ) => void;
}

export function TradePanel({
  isVisible,
  tradeType,
  strikePrice,
  spotPrice,
  asset,
  volatility,
  expiryTimestamp,
  side = 'BUY',
  maxQuantity,
  onClose,
  onTrade,
}: TradePanelProps) {
  const [amount, setAmount] = useState(1);
  const [amountInput, setAmountInput] = useState('1');
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT'>('MARKET');
  const [limitPrice, setLimitPrice] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showMaxProfitTooltip, setShowMaxProfitTooltip] = useState(false);

  // Get real user balance and option pricing
  const { balance } = useAccount();
  const {
    callPrice: _callPrice,
    putPrice: _putPrice,
    callAsk,
    putAsk,
    callBid,
    putBid,
  } = useOptionsPricing({
    spotPrice,
    strikePrice,
    asset,
    volatility,
  });

  // Get detailed pricing for the enhanced price picker
  const currentPricing = useMemo(() => {
    const effectiveExpiryTimestamp = expiryTimestamp ?? RoundService.getCurrentRoundExpiry();
    return tradeType
      ? orderService.calculatePricing(
          asset,
          strikePrice,
          tradeType,
          effectiveExpiryTimestamp,
          spotPrice,
          volatility,
        )
      : null;
  }, [asset, strikePrice, tradeType, spotPrice, volatility, expiryTimestamp]);

  // Reset limit price to mid when panel becomes visible
  useEffect(() => {
    if (isVisible && currentPricing) {
      console.log('[TradePanel] Setting limit price to mid (panel visible):', {
        mid: currentPricing.mid,
      });
      setLimitPrice(currentPricing.mid);
    }
    // Only run when panel visibility changes, not when pricing updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Reset limit price to mid when switching to limit orders
  useEffect(() => {
    if (orderType === 'LIMIT' && currentPricing) {
      console.log(
        '[TradePanel] Setting limit price to mid (order type changed):',
        {
          mid: currentPricing.mid,
        },
      );
      setLimitPrice(currentPricing.mid);
    }
    // Only run when order type changes, not when pricing updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  // Calculate real option pricing - use ask prices for buy orders, bid prices for sell orders
  // Use currentPricing (with volatility override) instead of useOptionsPricing values
  const premium =
    side === 'SELL'
      ? currentPricing?.bid ?? (tradeType === 'CALL' ? callBid : putBid)
      : currentPricing?.ask ?? (tradeType === 'CALL' ? callAsk : putAsk);
  const effectivePrice = orderType === 'LIMIT' ? limitPrice : premium;
  const totalValue = amount * effectivePrice;
  
  // For buy orders: cost, profit, loss
  // For sell orders: proceeds, profit, loss (reversed)
  const maxProfit =
    side === 'SELL'
      ? tradeType === 'CALL'
        ? `$${(effectivePrice * amount).toFixed(2)}`
        : 'Unlimited'
      : tradeType === 'CALL'
        ? 'Unlimited'
        : `$${((strikePrice - effectivePrice) * amount).toFixed(2)}`;
  const breakEven =
    side === 'SELL'
      ? tradeType === 'CALL'
        ? strikePrice - effectivePrice
        : strikePrice + effectivePrice
      : tradeType === 'CALL'
        ? strikePrice + effectivePrice
        : strikePrice - effectivePrice;
  const _maxLoss =
    side === 'SELL'
      ? tradeType === 'CALL'
        ? 'Unlimited'
        : `$${((strikePrice - effectivePrice) * amount).toFixed(2)}`
      : totalValue;
  const hasInsufficientFunds = side === 'BUY' && totalValue > balance;
  const exceedsMaxQuantity = side === 'SELL' && maxQuantity !== undefined && amount > maxQuantity;
  const isInvalidAmount = amount <= 0 || exceedsMaxQuantity;

  const presetAmounts = [0.01, 0.1, 1, 5, 10];

  // Format max profit for display
  const formatMaxProfit = (profitStr: string) => {
    if (profitStr === 'Unlimited') {
      return profitStr;
    }

    const profit = parseFloat(profitStr.replace(/[$,]/g, ''));
    if (profit >= 100000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(profit);
    }
    return profitStr;
  };

  const rawMaxProfit = maxProfit;
  const displayMaxProfit = formatMaxProfit(maxProfit);
  const shouldShowProfitTooltip =
    maxProfit !== 'Unlimited' &&
    parseFloat(maxProfit.replace(/[$,]/g, '')) >= 100000;

  const handleAmountChange = (inputValue: string) => {
    // Normalize input: if user types ".", display "0."
    let normalizedInput = inputValue;
    if (inputValue === '.') {
      normalizedInput = '0.';
    }

    setAmountInput(normalizedInput);

    // Handle empty string
    if (normalizedInput === '') {
      setAmount(0);
      return;
    }

    // Handle "0." or other incomplete decimals
    if (normalizedInput === '0.' || normalizedInput.endsWith('.')) {
      setAmount(parseFloat(normalizedInput) || 0);
      return;
    }

    const numValue = parseFloat(normalizedInput);
    if (!isNaN(numValue) && numValue >= 0) {
      // For sell orders, cap at maxQuantity if provided
      if (side === 'SELL' && maxQuantity !== undefined) {
        setAmount(Math.min(numValue, maxQuantity));
        // Update input to reflect capped value if it was exceeded
        if (numValue > maxQuantity) {
          setAmountInput(maxQuantity.toString());
        }
      } else {
        setAmount(numValue);
      }
    }
  };

  const handleTrade = () => {
    const price = orderType === 'LIMIT' ? limitPrice : undefined;
    console.log('[TradePanel] handleTrade called:', {
      orderType,
      limitPrice,
      price,
      effectivePrice,
      amount,
    });
    onTrade(amount, orderType, price, effectivePrice);
    onClose();
  };

  if (!isVisible || !tradeType) {
    return null;
  }

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="flex-shrink-0 bg-brand-dark/95 backdrop-blur-sm border-t-2 border-divider overflow-hidden"
      style={{
        maxHeight: '50vh',
      }}
    >
      <div className="p-4 space-y-3">
        {/* Amount and Price Grid */}
        <div className="grid grid-cols-4 gap-3">
          {/* Left Column - Amount (1 column = 25%) */}
          <div className="col-span-1 space-y-2">
            {/* Amount Label */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-label text-xs">AMOUNT</span>
            </div>
            {/* Amount Input */}
            <input
              type="text"
              inputMode="decimal"
              value={amountInput}
              onChange={e => handleAmountChange(e.target.value)}
              onFocus={e => e.target.select()}
              className="w-full bg-surface text-white border border-border-default focus:border-border-hover focus:outline-none text-lg font-mono p-2 rounded"
              step="0.01"
              min="0"
              max={side === 'SELL' && maxQuantity !== undefined ? maxQuantity : undefined}
            />
          </div>

          {/* Right Column - Price (3 columns = 75%) */}
          <div className="col-span-3 space-y-2">
            {/* Price Label and Order Type */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-label">
                {orderType === 'MARKET' ? 'MARKET PRICE' : 'LIMIT PRICE'}
              </span>
              <button
                onClick={() =>
                  setOrderType(orderType === 'MARKET' ? 'LIMIT' : 'MARKET')
                }
                className="text-white text-xs flex items-center space-x-1"
              >
                <span>
                  {orderType === 'MARKET' ? 'Market Order' : 'Limit Order'}
                </span>
                <span className="text-label">▼</span>
              </button>
            </div>

            {/* Price Input/Picker */}
            {orderType === 'MARKET' ? (
              <div className="bg-surface/50 border border-border-subtle rounded p-2 text-center">
                <div className="text-lg font-mono text-label">
                  {formatOptionPrice(premium)}
                </div>
              </div>
            ) : (
              <BidAskMidPicker
                pricing={currentPricing}
                selectedPrice={limitPrice}
                onPriceChange={setLimitPrice}
                isLoading={!currentPricing}
                disabled={false}
              />
            )}
          </div>
        </div>

        {/* Preset Amount Buttons */}
        <div className="flex space-x-2">
          {presetAmounts.map(preset => (
            <button
              key={preset}
              onClick={() => {
                setAmount(preset);
                setAmountInput(preset.toString());
              }}
              className={`flex-1 py-1 rounded text-sm font-medium ${
                amount === preset
                  ? 'bg-white text-black'
                  : 'bg-surface text-label hover:bg-surface-hover'
              }`}
            >
              {preset}
            </button>
          ))}
        </div>

        {/* Trade Stats Grid */}
        <div className="grid grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-label flex items-center gap-1">
              {side === 'SELL' ? 'EST. PROCEEDS' : 'EST. COST'}
              <div className="relative">
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setShowTooltip(!showTooltip);
                  }}
                  className="w-3 h-3 bg-surface/80 hover:bg-label/60 rounded-full flex items-center justify-center text-white text-[8px] cursor-pointer transition-colors leading-none"
                >
                  ?
                </button>
                {showTooltip && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-brand-dark text-white text-xs rounded whitespace-nowrap z-10">
                    {side === 'SELL' ? '(Max Profit)' : '(Max Loss)'}
                  </div>
                )}
              </div>
            </div>
            <div className="text-white font-bold text-lg">
              ${totalValue.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-label">BALANCE</div>
            <div className="text-white text-lg">${balance.toFixed(2)}</div>
          </div>
          <div>
            <div className="text-label">MAX PROFIT</div>
            <div className="relative">
              {shouldShowProfitTooltip ? (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setShowMaxProfitTooltip(!showMaxProfitTooltip);
                  }}
                  className="text-green-400 text-lg cursor-pointer hover:text-green-300 transition-colors"
                >
                  {displayMaxProfit}
                </button>
              ) : (
                <div className="text-green-400 text-lg">{displayMaxProfit}</div>
              )}
              {showMaxProfitTooltip && shouldShowProfitTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-brand-dark text-white text-xs rounded whitespace-nowrap z-10">
                  {rawMaxProfit}
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="text-label">BREAK-EVEN</div>
            <div className="text-white text-lg">${breakEven.toFixed(2)}</div>
          </div>
        </div>

        {/* Slide to Confirm Buy Button */}
        <SlideToConfirmButton
          onConfirm={handleTrade}
          disabled={hasInsufficientFunds || isInvalidAmount}
          className={`w-full ${
            hasInsufficientFunds
              ? 'bg-surface/80'
              : tradeType === 'CALL'
                ? 'bg-green-600'
                : 'bg-red-500/80'
          }`}
          confirmText="RELEASE TO CONFIRM"
          data-testid="slide-to-confirm-trade"
        >
          {hasInsufficientFunds ? (
            'INSUFFICIENT FUNDS'
          ) : exceedsMaxQuantity ? (
            `AMOUNT CANNOT EXCEED (${maxQuantity})`
          ) : isInvalidAmount ? (
            'INVALID AMOUNT'
          ) : (
            <span>
              SLIDE TO {side === 'SELL' ? 'SELL' : 'BUY'}{' '}
              {tradeType === 'CALL' ? 'ABOVE' : 'BELOW'} • $
              {totalValue.toFixed(2)}
            </span>
          )}
        </SlideToConfirmButton>
      </div>
    </motion.div>
  );
}
